import type { User } from "@supabase/supabase-js";
import { useEffect, useRef, useState } from "react";
import { useOAuthCompletion } from "../../../hooks/useOAuthCompletion";
import { useOAuthConsumptionWatcher } from "../../../hooks/useOAuthConsumptionWatcher";
import DesktopRedirectingView from "../../DesktopRedirectingView";

interface Props {
    sessionId: string;
    user: User | null;
    loading: boolean;
}

export default function DesktopAuthFlow({ sessionId, user, loading }: Props) {
    const oauthCompletion = useOAuthCompletion(sessionId);
    const consumed = useOAuthConsumptionWatcher(sessionId);
    const [showRetry, setShowRetry] = useState(false);

    useEffect(() => {
        if (!loading && user && sessionId && !oauthCompletion.isRedirecting) {
            console.log("🖥️ [AUTH] Desktop Flow - Auto-completing OAuth");
            // Fire the dcapp:// deep link immediately. The parallel poll that used to
            // deliver the code is gone, so without this the app would only reach the
            // deep link via the 10s fallback timer below — a 10s wait on every login.
            oauthCompletion.handleComplete(true);
        }
    }, [
        loading,
        user,
        sessionId,
        oauthCompletion,
    ]);

    // `oauthCompletion.retry` is a new function whenever `user` changes identity,
    // which AuthContext does on every session poll. Reading it through a ref keeps
    // it out of the dependency list below, so the fallback timer is armed once when
    // the deep link appears instead of being re-armed on every poll.
    const retryRef = useRef(oauthCompletion.retry);
    retryRef.current = oauthCompletion.retry;

    // The fallback re-fires the deep link once if the app never picked up the
    // first one. The auth code is single-use, so retrying it repeatedly can only
    // relaunch the app with a code the server has already spent.
    const fallbackFired = useRef(false);

    useEffect(() => {
        if (!oauthCompletion.redirectUri || consumed || fallbackFired.current) {
            return;
        }

        const fallback = window.setTimeout(() => {
            fallbackFired.current = true;
            retryRef.current();
        }, 10_000);
        return () => window.clearTimeout(fallback);
    }, [
        consumed,
        oauthCompletion.redirectUri,
    ]);

    useEffect(() => {
        if (loading || consumed || showRetry) {
            return;
        }

        const timeout = window.setTimeout(() => setShowRetry(true), 15_000);
        return () => window.clearTimeout(timeout);
    }, [
        loading,
        consumed,
        showRetry,
    ]);

    return (
        <DesktopRedirectingView
            userEmail={user?.email}
            onRetry={() => {
                setShowRetry(false);
                oauthCompletion.retry();
            }}
            connected={consumed}
            showRetry={showRetry}
        />
    );
}
